import { useNavigate } from 'react-router-dom';

import { EmptyScreen, Spinner } from '@components';
import CustomTable from '@components/custom-table/CustomTable';
import { CustomTableRowData } from '@components/custom-table/types';
import CustomTooltip from '@components/tooltip/Tooltip';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { LEAD_MANAGEMENT } from '@constants/routes';
import ActionButton from '@features/lead-management/components/ActionButton';
import { leadsTableColumn } from '@features/lead-management/constants/leadList';
import { formatMobileNumber } from '@features/lead-management/utils/common';
import useIsTruncated from '@hooks/useIsTruncated';
import { StatusEntity } from 'types/lead-management';

import { LeadTableProps } from './types';

import LeadAssignee from '../../components/LeadAssignee';
import LeadStatusTag from '../../components/LeadStatusTag';
import LeadTags from '../../components/LeadTags';
import { LeadSource, leadSourceOptions } from '../../constants';
import { UNASSIGNED_ASSIGNEE } from '../../constants/common';
import { LeadType } from '../../types/common';

const SourceCell = ({
  text,
  fullText,
  tooltipId
}: {
  text: string;
  fullText: string;
  tooltipId: string;
}) => {
  const { ref, isTruncated } = useIsTruncated<HTMLDivElement>(text);
  return (
    <>
      <div
        ref={ref}
        data-tooltip-id={isTruncated ? tooltipId : undefined}
        className="max-w-45 truncate text-base font-medium"
      >
        {text}
      </div>
      {isTruncated && (
        <CustomTooltip
          id={tooltipId}
          type="text"
          tooltipText={fullText}
          place="top"
        />
      )}
    </>
  );
};

const LeadTable = ({
  page,
  setPage,
  selectedRows,
  setSelectedRows,
  onUpdateAssignee,
  leadDetails,
  handleAddTagButtonClick,
  agentsList,
  handleDeleteLead,
  isListLoading,
  isAssigneeDisabled = false
}: LeadTableProps) => {
  const navigate = useNavigate();
  const leadList = leadDetails?.data ?? [];
  const pagination = leadDetails?.pagination;

  const getAssigneeOptions = () => [
    UNASSIGNED_ASSIGNEE,
    ...agentsList.map((agent) => ({
      id: agent.user_id,
      name: agent.name
    }))
  ];

  const handleAssigneeSelect = (
    leadId: string,
    assignee: {
      id: string;
      isUnassigned: boolean;
    }
  ) => {
    if (onUpdateAssignee && assignee) {
      onUpdateAssignee(leadId, assignee);
    }
  };

  const handleEditLead = (leadId: string) => {
    if (leadId) {
      navigate(LEAD_MANAGEMENT.LEAD_EDIT.replace(':leadId', leadId));
    }
  };

  const renderTableCell = (value: string) => (
    <div className="max-w-45 truncate text-base font-medium">
      {value || DEFAULT_PLACEHOLDER}
    </div>
  );

  const getSourceDisplay = (row: LeadType) => {
    const sourceKey = row.source as keyof typeof leadSourceOptions;
    const baseSource = leadSourceOptions[sourceKey] || DEFAULT_PLACEHOLDER;
    const tooltipId = `source-tooltip-${row.id}`;

    if (row.source === LeadSource.FACEBOOK_ADS && row.ad?.name) {
      const displayText = `Facebook Ad: ${row.ad.name}`;
      return (
        <SourceCell
          text={displayText}
          fullText={displayText}
          tooltipId={tooltipId}
        />
      );
    }

    return (
      <SourceCell
        text={baseSource}
        fullText={baseSource}
        tooltipId={tooltipId}
      />
    );
  };

  const modifiedTableData = leadList.map((row: LeadType) => ({
    id: row.id,
    customerName: renderTableCell(row.name),
    mobileNumber: renderTableCell(
      formatMobileNumber(row.mobile_number, row.country_code)
    ),
    tags: (
      <div className="max-w-70">
        <LeadTags
          tags={row.tags}
          handleAddTagButtonClick={() => handleAddTagButtonClick?.(row.id)}
          showAddButton={false}
        />
      </div>
    ),
    source: getSourceDisplay(row),
    assignee: (
      <LeadAssignee
        assignee={{
          id: row.assignee?.id || row.assignee?.user_id,
          name: row.assignee?.name || '',
          isUnassigned: !row.assignee?.name
        }}
        assigneeOptions={getAssigneeOptions()}
        handleAssigneeSelect={(assignee) =>
          handleAssigneeSelect(row.id, assignee)
        }
        disabled={isAssigneeDisabled}
      />
    ),
    status: <LeadStatusTag status={row.status as StatusEntity} />,
    actions: (
      <ActionButton
        item={row}
        handleEditAction={() => handleEditLead(row.id)}
        handleDeleteAction={() => handleDeleteLead(row.id)}
        actions={['edit', 'delete']}
      />
    )
  }));

  const handleRowClick = (row: CustomTableRowData<string>) => {
    navigate(`${LEAD_MANAGEMENT.LEAD_DETAILS.replace(':leadId', row.id)}`);
  };

  const handleSelect = (row: CustomTableRowData<string>) => {
    if (selectedRows.includes(row.id)) {
      setSelectedRows(selectedRows.filter((id) => id !== row.id));
    } else {
      setSelectedRows([...selectedRows, row.id]);
    }
  };

  const handleSelectAll = (selectedIds: string[], removedIds: string[]) => {
    if (selectedIds.length > 0) {
      setSelectedRows([...selectedRows, ...selectedIds]);
    }
    if (removedIds.length > 0) {
      setSelectedRows(selectedRows.filter((id) => !removedIds.includes(id)));
    }
  };

  if (isListLoading) {
    return (
      <div className="mt-4 flex min-h-[50vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!leadList.length) {
    return (
      <div className="mt-4 min-h-[50vh]">
        <EmptyScreen />
      </div>
    );
  }

  return (
    <div className="mt-4">
      <CustomTable
        headers={leadsTableColumn}
        selectedRows={selectedRows}
        handleSelect={handleRowClick}
        handleCheckboxSelect={handleSelect}
        handleSelectAll={handleSelectAll}
        data={modifiedTableData ?? []}
        totalCount={pagination?.total_rows ?? 0}
        currentPage={page + 1}
        onPageChange={(count: number) => setPage(count - 1)}
      />
    </div>
  );
};

export default LeadTable;
